import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, NavLink } from 'react-router-dom';
import { useForm } from 'react-hook-form'
import { Error, Spinner } from '../../common/Error';
import { FormSelect, FormCheckbox } from '../../common/form'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Hero from '../../common/Hero'
import {
  doRegister,
  selectUser,
} from './authSlice';
import { useListLanguagesQuery, INITIAL_LANGUAGES} from '../../app/services/languages/languagesService'

export const Register = () => {
  const user = useSelector(selectUser)
  const { loading, error, success } = useSelector((state) => state.auth.register)
  const dispatch = useDispatch()
  const email = localStorage.getItem('email')
  //console.log('[Register] email=', email)
  //console.log('[Register] error=', error)
  const { data: languages = INITIAL_LANGUAGES } = useListLanguagesQuery()

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string()
      .required('Email is required')
      .email('Email is invalid'),
    password: Yup.string()
      .required('Password is required')
      .min(4, 'Password must be at least 4 characters')
      .max(32, 'Password must not exceed 32 characters'),
    confirmPassword: Yup.string()
      .required('Confirm Password is required')
      .oneOf([Yup.ref('password'), null], 'Confirm Password does not match'),
    primaryLanguage: Yup.string()
      .required('Primary Language is required'),
    chatLanguage: Yup.string()
      .required('Chat Language is required'),
    agreement: Yup.boolean()
      .required("You must agree to the terms to use Babel Chat.")
      .oneOf([true], "You must agree to the terms to use Babel Chat.")
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors } 
  } = useForm({
    defaultValues: {
      name: '',
      email: (email === undefined || email === null) ? '' : email,
      password: '',
      confirmPassword: '',
      primaryLanguage: window.navigator.language.slice(0, 2),
      chatLanguage: window.navigator.language.slice(0, 2),
    },
    resolver: yupResolver(validationSchema)
  })

  const navigate = useNavigate()
  const language_options = languages.map((l) => {
    return {
      value: l.language,
      name: `${l.name} [${l.language}]`,
    }
  })

  useEffect(() => {
    // redirect user to verification page if registration was successful
    if (success) navigate('/verify')
    // redirect authenticated user to profile screen
    if (user) navigate('/rooms')
  }, [navigate, user, success])

  const submitForm = (data) => {
    //console.log('submitForm, data=', data)
    // transform email string to lowercase to avoid case sensitivity issues in login
    data.email = data.email.toLowerCase()
    dispatch(doRegister({
      ...data,
      native_lang_id: data.primaryLanguage,
      default_lang_id: data.chatLanguage
    }))
  }
  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Hero />
        <Typography component="h1" variant="h6">
          Create a Babel Chat account
        </Typography>
        <Box component='form' noValidate='noValidate' onSubmit={handleSubmit(submitForm)}>
          <TextField
            id='name'
            autoComplete='full-name'
            name='name'
            {...register('name')}
            required
            fullWidth
            label='Name'
            margin='normal'
            autoFocus
            error={errors.name ? true : false}
            helperText={errors.name?.message}
          />
          <TextField
            id='email'
            autoComplete='email'
            name='email'
            {...register('email')}
            required
            fullWidth
            label='Email'
            margin='normal'
            disabled={email !== undefined && email !== null}
            error={errors.email ? true : false}
            helperText={errors.email?.message}
          />
          <TextField
            id='password'
            autoComplete='new-password'
            type='password'
            name='password'
            {...register('password')}
            required
            fullWidth
            margin='normal'
            label='Password'
            error={errors.password ? true : false}
            helperText={errors.password?.message}
          />
          <TextField
            id='confirmPassword'
            autoComplete='new-password'
            type='password'
            name='confirmPassword'
            {...register('confirmPassword')}
            required
            fullWidth
            margin='normal'
            label='Confirm Password'
            error={errors.confirmPassword ? true : false}
            helperText={errors.confirmPassword?.message}
          />
          <FormSelect
            id='primary-language'
            name='primaryLanguage'
            label='Primary Language'
            control={control}
            variant='outlined'
            margin='normal'
            required
            fullWidth
            options={language_options}
            error={errors.primaryLanguage ? true : false}
            helperText={'Select the language you use on a daily basis'}
          />
          <FormSelect
            id='chat-language'
            name='chatLanguage'
            label='Chat Language'
            control={control}
            variant='outlined'
            margin='normal'
            required
            fullWidth
            options={language_options}
            error={errors.chatLanguage ? true : false}
            helperText={'Select the language you will use to chat'}
          />
          <FormCheckbox
            id='agreement'
            name='agreement'
            control={control}
            defaultValue={false}
            label={[
              "I agree to the ",
              { text: "Terms", href: process.env.REACT_APP_API_BASE_URL + "/Babel_Chat_Terms_Mar2024.pdf" },
              " and ",
              { text: "Privacy Policy", href: process.env.REACT_APP_API_BASE_URL + "/Babel_Chat_Privacy_Policy_Mar2024.pdf" },
              "."
            ]}
            error={errors.agreement ? true : false}
            helperText={errors.agreement?.message}
          />
          <LoadingButton
            size='large'
            type='submit'
            fullWidth
            variant='contained'
            loading={loading}
            loadingPosition="start"
            startIcon={<HowToRegIcon />}
            sx={{ mt: 4, mb: 4 }}
          >
            Register
          </LoadingButton>
        </Box>
        <Link
          component={NavLink}
          to={'/login'}
          variant="body1"
          sx={{
            mt: 2,
            mb: 2
          }}
        >
          Already have an account? Log in
        </Link>
      </Box>
    </Container>
  )
}

